
import { useState } from "react";
import { Upload, Loader2 } from "lucide-react";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";

interface PhotoUploadFormProps {
  photoTitle: string;
  setPhotoTitle: (title: string) => void;
  photoCategory: string;
  setPhotoCategory: (category: string) => void;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isUploading: boolean;
}

export function PhotoUploadForm({
  photoTitle,
  setPhotoTitle,
  photoCategory,
  setPhotoCategory,
  onFileSelect,
  isUploading
}: PhotoUploadFormProps) {
  const { language } = useLanguage();

  return (
    <div className="space-y-4 py-4">
      <div className="grid gap-2">
        <label htmlFor="photoTitle" className="text-sm font-medium">
          {t('gallery.photoTitle', language)}
        </label>
        <input
          id="photoTitle"
          type="text"
          placeholder={t('gallery.enterPhotoTitle', language)}
          className="w-full px-3 py-2 border rounded-md"
          value={photoTitle}
          onChange={(e) => setPhotoTitle(e.target.value)}
        />
      </div>
      <div className="grid gap-2">
        <label htmlFor="photoCategory" className="text-sm font-medium">
          {t('gallery.category', language)}
        </label>
        <select
          id="photoCategory"
          className="w-full px-3 py-2 border rounded-md"
          value={photoCategory}
          onChange={(e) => setPhotoCategory(e.target.value)}
        >
          <option value="recent">{t('gallery.recentPhoto', language)}</option>
          <option value="milestone">{t('gallery.milestonePhoto', language)}</option>
          <option value="first-year">{t('gallery.firstYear', language)}</option>
          <option value="family-moments">{t('gallery.familyMoments', language)}</option>
          <option value="special-events">{t('gallery.specialEvents', language)}</option>
        </select>
      </div>
      <div className="grid gap-2">
        <label htmlFor="photoUpload" className="text-sm font-medium">
          {t('gallery.choosePhoto', language)}
        </label>
        <label
          className="flex items-center justify-center gap-2 px-4 py-2 cursor-pointer border border-dashed rounded-md hover:bg-gray-50 transition-colors"
        >
          <Upload size={20} />
          <span>{t('gallery.selectImageFile', language)}</span>
          <input
            id="photoUpload"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onFileSelect}
            disabled={isUploading}
          />
        </label>
      </div>
      {isUploading && (
        <div className="flex justify-center items-center py-2">
          <Loader2 className="animate-spin mr-2" size={20} />
          <span>{t('gallery.uploading', language)}</span>
        </div>
      )}
    </div>
  );
}
